import React, { useEffect, useRef, useState } from 'react';
import { Bell, ChevronDown, ExternalLink, LogOut, Menu, MessageSquare, X } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';

interface NavItem {
  name: string;
  path: string;
  external?: boolean;
  glow?: boolean;
}

const programLinks: NavItem[] = [
  { name: 'Programs', path: '/services' },
  { name: 'Curriculum', path: '/curriculum' },
  { name: 'Accelerator', path: '/accelerator' },
  { name: 'Pricing', path: '/pricing' },
];

const moreLinks: NavItem[] = [
  { name: 'Gallery', path: '/gallery' },
  { name: 'Staff', path: '/planner' },
  { name: 'Contact', path: '/contact' },
  { name: 'GiIT LMS', path: 'https://lms.giitlearn.com/', external: true },
];

const primaryLinks: NavItem[] = [
  { name: 'Home', path: '/' },
  { name: 'Explore', path: '/explore', glow: true },
  { name: 'About', path: '/about' },
];

const homeSectionLinks = [
  { name: 'Top', id: 'home-top' },
  { name: 'Approach', id: 'about' },
  { name: 'Labs', id: 'labs' },
  { name: 'Talent', id: 'talent' },
  { name: 'Enroll', id: 'home-enroll' },
];

const adminWorkspaceLinks = [
  { name: 'New Post', key: 'create' },
  { name: 'Posts', key: 'manage' },
  { name: 'Gallery', key: 'gallery' },
  { name: 'Settings', key: 'settings' },
  { name: 'Pricing', key: 'pricing' },
  { name: 'Programs', key: 'programs' },
  { name: 'Accelerator', key: 'accelerator' },
  { name: 'Staff', key: 'staff' },
];

const staffWorkspaceLinks = [
  { name: 'Dashboard', key: 'dashboard' },
  { name: 'Goals', key: 'goals' },
  { name: 'Calendar', key: 'calendar' },
  { name: 'Messages', key: 'messages' },
  { name: 'Reports', key: 'reports' },
  { name: 'Leave', key: 'leave' },
  { name: 'Files', key: 'files' },
  { name: 'Notifications', key: 'notifications' },
  { name: 'Settings', key: 'settings' },
];

function isActivePath(currentPath: string, item: NavItem) {
  return !item.external && currentPath === item.path;
}

function scrollToHomeSection(sectionId: string) {
  const element = document.getElementById(sectionId);

  if (!element) {
    return;
  }

  const headerOffset = window.innerWidth < 1024 ? 124 : 132;
  const top = element.getBoundingClientRect().top + window.scrollY - headerOffset;

  window.scrollTo({
    top: Math.max(top, 0),
    behavior: 'smooth',
  });
}

function NavLinkItem({
  item,
  active,
  onClick,
}: {
  item: NavItem;
  active?: boolean;
  onClick?: () => void;
}) {
  const className = `inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] transition-colors ${
    active ? 'text-white' : 'text-slate-400 hover:text-white'
  }${item.glow ? ' nav-glow' : ''}`;

  if (item.external) {
    return (
      <a href={item.path} target="_blank" rel="noopener noreferrer" className={className} onClick={onClick}>
        {item.name}
        <ExternalLink className="h-3.5 w-3.5" />
      </a>
    );
  }

  return (
    <Link to={item.path} className={className} onClick={onClick}>
      {item.name}
    </Link>
  );
}

function MobileMenuRow({
  item,
  active,
  onNavigate,
}: {
  item: NavItem;
  active?: boolean;
  onNavigate: () => void;
}) {
  const className = `flex min-h-14 w-full items-center justify-between rounded-2xl px-4 py-3 text-left text-[12px] font-black uppercase tracking-[0.18em] transition-colors active:scale-[0.99] ${
    active ? 'bg-sky-500/20 text-white ring-1 ring-sky-400/30' : 'bg-white/[0.06] text-slate-100'
  }${item.glow ? ' nav-glow' : ''}`;

  if (item.external) {
    return (
      <a href={item.path} target="_blank" rel="noopener noreferrer" className={className} onClick={onNavigate}>
        <span>{item.name}</span>
        <ExternalLink className="h-4 w-4" />
      </a>
    );
  }

  return (
    <Link to={item.path} className={className} onClick={onNavigate}>
      <span>{item.name}</span>
    </Link>
  );
}

function DesktopMenu({
  label,
  items,
  active,
}: {
  label: string;
  items: NavItem[];
  active: boolean;
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (target && menuRef.current && !menuRef.current.contains(target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown, { passive: true });

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [open]);

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] transition-colors ${
          active ? 'text-white' : 'text-slate-400 hover:text-white'
        }`}
      >
        {label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-5 w-56 overflow-hidden rounded-2xl border border-white/10 bg-brand-dark/95 p-2 shadow-2xl shadow-black/30 backdrop-blur-xl">
          {items.map((item) => (
            <div key={item.path} className="rounded-xl px-3 py-2 transition-colors hover:bg-white/8">
              <NavLinkItem item={item} onClick={() => setOpen(false)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileOpenGroups, setMobileOpenGroups] = useState<Record<string, boolean>>({
    Main: true,
    Programs: false,
    More: false,
  });
  const [workspaceActive, setWorkspaceActive] = useState('dashboard');
  const [workspaceCounts, setWorkspaceCounts] = useState({ messages: 0, notifications: 0 });
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef<HTMLElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setWorkspaceActive(location.pathname === '/admin' ? 'create' : 'dashboard');
    setWorkspaceCounts({ messages: 0, notifications: 0 });
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) return;

    const closeOnOutsidePress = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (mobileMenuRef.current?.contains(target)) return;
      if (navRef.current?.contains(target)) return;
      setMobileMenuOpen(false);
    };

    document.addEventListener('mousedown', closeOnOutsidePress);
    document.addEventListener('touchstart', closeOnOutsidePress, { passive: true });

    return () => {
      document.removeEventListener('mousedown', closeOnOutsidePress);
      document.removeEventListener('touchstart', closeOnOutsidePress);
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const handleCounts = (event: Event) => {
      const detail = (event as CustomEvent<{ messages?: number; notifications?: number }>).detail;
      setWorkspaceCounts({
        messages: Math.max(0, Number(detail?.messages) || 0),
        notifications: Math.max(0, Number(detail?.notifications) || 0),
      });
    };
    window.addEventListener('giit-workspace-counts', handleCounts);
    return () => window.removeEventListener('giit-workspace-counts', handleCounts);
  }, []);

  const programActive = programLinks.some((item) => isActivePath(location.pathname, item));
  const moreActive = moreLinks.some((item) => isActivePath(location.pathname, item));
  const isHome = location.pathname === '/';
  const isWorkspace = ['/admin', '/staff', '/planner'].includes(location.pathname);
  const workspaceLinks = location.pathname === '/admin' ? adminWorkspaceLinks : staffWorkspaceLinks;
  const mobileGroups = [
    { label: 'Main', items: primaryLinks },
    { label: 'Programs', items: programLinks },
    { label: 'More', items: moreLinks },
  ];

  const handleWorkspaceLogout = () => {
    if (location.pathname === '/admin') {
      localStorage.removeItem('giit-admin-token');
      navigate('/admin', { replace: true });
    } else {
      localStorage.removeItem('giit-staff-token');
      localStorage.removeItem('giit-staff-account');
      navigate('/planner', { replace: true });
    }
    window.setTimeout(() => window.location.reload(), 20);
  };

  const handleWorkspaceNav = (key: string) => {
    setWorkspaceActive(key);
    window.dispatchEvent(new CustomEvent(location.pathname === '/admin' ? 'giit-admin-nav' : 'giit-staff-nav', {
      detail: { key },
    }));
  };

  const handleWorkspaceQuickNav = (key: 'messages' | 'notifications') => {
    if (location.pathname === '/admin') {
      setWorkspaceActive('staff');
      window.dispatchEvent(new CustomEvent('giit-admin-nav', { detail: { key: 'staff', staffView: key } }));
      window.setTimeout(() => window.dispatchEvent(new CustomEvent('giit-admin-staff-nav', { detail: { view: key } })), 40);
      return;
    }
    handleWorkspaceNav(key);
  };

  const toggleMobileGroup = (group: string) => {
    setMobileOpenGroups(prev => {
      const isOpen = Boolean(prev[group]);
      return {
        Main: false,
        Programs: false,
        More: false,
        [group]: !isOpen,
      };
    });
  };

  useEffect(() => {
    if (!isWorkspace) return;
    let cancelled = false;
    const loadCounts = async () => {
      try {
        if (location.pathname === '/admin') {
          const token = localStorage.getItem('giit-admin-token');
          if (!token) return;
          const [notificationsRes, conversationsRes] = await Promise.all([
            fetch('/api/hr/admin/notifications', { headers: { Authorization: `Bearer ${token}` } }),
            fetch('/api/hr/admin/conversations', { headers: { Authorization: `Bearer ${token}` } }),
          ]);
          if (!notificationsRes.ok || !conversationsRes.ok) return;
          const notifications = await notificationsRes.json();
          const conversations = await conversationsRes.json();
          const unreadNotifications = (notifications.data || []).filter((item: { readAt?: string | null }) => !item.readAt).length;
          const unreadMessagesFromNotifications = (notifications.data || []).filter((item: { readAt?: string | null; type?: string }) => !item.readAt && item.type === 'message').length;
          const staffReplies = (conversations.data || []).filter((thread: { messages?: Array<{ senderType?: string }>; readBy?: Record<string, string> }) => thread.messages?.at(-1)?.senderType === 'staff' && !thread.readBy?.admin).length;
          if (!cancelled) setWorkspaceCounts({ messages: Math.max(unreadMessagesFromNotifications, staffReplies), notifications: unreadNotifications });
          return;
        }
        const token = localStorage.getItem('giit-staff-token');
        if (!token) return;
        const [dashboardRes, notificationsRes] = await Promise.all([
          fetch('/api/hr/staff/dashboard', { headers: { Authorization: `Bearer ${token}` } }),
          fetch('/api/hr/staff/notifications', { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (!dashboardRes.ok || !notificationsRes.ok) return;
        const dashboard = await dashboardRes.json();
        const notifications = await notificationsRes.json();
        if (!cancelled) {
          setWorkspaceCounts({
            messages: Number(dashboard.data?.unreadMessages || 0),
            notifications: (notifications.data || []).filter((item: { readAt?: string | null }) => !item.readAt).length,
          });
        }
      } catch {
        // Counts are helpful but should never break navigation.
      }
    };
    loadCounts();
    const timer = window.setInterval(loadCounts, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [isWorkspace, location.pathname]);

  return (
    <nav
      ref={navRef}
      id="main-nav"
      className={`fixed left-0 right-0 top-0 z-50 transition-all duration-500 ${
        isScrolled ? 'border-b border-white/5 bg-brand-dark/88 py-4 shadow-2xl shadow-black/20 backdrop-blur-xl' : 'bg-transparent py-6 lg:py-8'
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 sm:px-8">
        <Link to="/" className="group flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl bg-white transition-transform duration-300 group-hover:scale-105 sm:h-12 sm:w-12 sm:rounded-2xl">
            <img src="/images/logo.png" alt="GIIT Logo" className="h-full w-full object-contain" referrerPolicy="no-referrer" />
          </div>
          <span className="editorial-title text-2xl text-white sm:text-3xl">GIITLEARN</span>
        </Link>

        {isWorkspace ? (
          <>
            <div className="mx-4 hidden min-w-0 flex-1 justify-center lg:flex">
              <div className="flex max-w-[min(58vw,56rem)] items-center gap-1 overflow-x-auto rounded-full border border-white/10 bg-black/10 p-1.5 backdrop-blur-xl">
                {workspaceLinks.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => handleWorkspaceNav(item.key)}
                    className={`whitespace-nowrap rounded-full px-3 py-2 text-[10px] font-bold uppercase tracking-[0.16em] transition-colors ${
                      workspaceActive === item.key ? 'bg-white text-brand-dark' : 'text-slate-400 hover:bg-white/8 hover:text-white'
                    }`}
                  >
                    {item.name}
                    {item.key === 'messages' && workspaceCounts.messages > 0 && <span className="ml-1 rounded-full bg-sky-500 px-1.5 py-0.5 text-[9px] leading-none text-white">{workspaceCounts.messages}</span>}
                    {item.key === 'notifications' && workspaceCounts.notifications > 0 && <span className="ml-1 rounded-full bg-amber-400 px-1.5 py-0.5 text-[9px] leading-none text-brand-dark">{workspaceCounts.notifications}</span>}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                type="button"
                onClick={() => handleWorkspaceQuickNav('messages')}
                className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/8 text-slate-200 transition-colors hover:bg-white/12 hover:text-white"
                aria-label="Open unread messages"
              >
                <MessageSquare className="h-4 w-4" />
                {workspaceCounts.messages > 0 && <span className="absolute -right-1 -top-1 min-w-5 rounded-full bg-sky-500 px-1.5 py-0.5 text-[10px] font-black leading-none text-white">{workspaceCounts.messages}</span>}
              </button>
              <button
                type="button"
                onClick={() => handleWorkspaceQuickNav('notifications')}
                className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/8 text-slate-200 transition-colors hover:bg-white/12 hover:text-white"
                aria-label="Open unread notifications"
              >
                <Bell className="h-4 w-4" />
                {workspaceCounts.notifications > 0 && <span className="absolute -right-1 -top-1 min-w-5 rounded-full bg-amber-400 px-1.5 py-0.5 text-[10px] font-black leading-none text-brand-dark">{workspaceCounts.notifications}</span>}
              </button>
              <ThemeToggle className="h-10 sm:px-2.5" />
              <button
                type="button"
                onClick={handleWorkspaceLogout}
                className="inline-flex items-center gap-2 rounded-full border border-rose-400/25 bg-white/8 px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-rose-200 transition-colors hover:border-rose-300/50 hover:bg-rose-500/10 sm:px-4"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </>
        ) : (
        <div className="hidden items-center gap-8 lg:flex">
          <div className="flex items-center gap-7">
            {primaryLinks.map((item) => (
              <NavLinkItem key={item.path} item={item} active={isActivePath(location.pathname, item)} />
            ))}
            <DesktopMenu label="Programs" items={programLinks} active={programActive} />
            <DesktopMenu label="More" items={moreLinks} active={moreActive} />
          </div>
          <ThemeToggle className="h-10" />
          <Link
            to="/enroll"
            className="rounded-full bg-brand-primary px-8 py-3 text-xs font-bold uppercase tracking-widest text-white shadow-2xl shadow-brand-primary/20 transition-all duration-300 hover:bg-white hover:text-brand-dark active:scale-95"
          >
            Apply Now
          </Link>
        </div>
        )}

        {!isWorkspace && <div className="flex items-center gap-2 lg:hidden">
          <Link
            to="/explore"
            onClick={() => setMobileMenuOpen(false)}
            className={`nav-glow inline-flex min-h-10 items-center justify-center rounded-full border px-3 text-[10px] font-black uppercase tracking-[0.18em] transition-colors ${
              location.pathname === '/explore'
                ? 'border-sky-400 bg-sky-500 text-white shadow-lg shadow-sky-500/20'
                : 'border-white/10 bg-white/8 text-sky-100 hover:bg-white/12'
            }`}
          >
            Feeds
          </Link>
          <ThemeToggle className="h-10 sm:px-2.5" />
          <button type="button" className="text-white" onClick={() => setMobileMenuOpen((value) => !value)} aria-label="Toggle navigation">
            {mobileMenuOpen ? <X size={30} /> : <Menu size={30} />}
          </button>
        </div>}
      </div>

      {isHome && !isWorkspace && (
        <div className="mx-auto mt-4 max-w-7xl px-6 sm:px-8">
          <div className="flex items-center gap-2 overflow-x-auto rounded-full border border-white/10 bg-black/18 p-1.5 shadow-2xl shadow-black/10 backdrop-blur-xl lg:inline-flex">
            {homeSectionLinks.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => scrollToHomeSection(item.id)}
                className="whitespace-nowrap rounded-full px-4 py-2 text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400 transition-colors hover:bg-white/8 hover:text-white"
              >
                {item.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {mobileMenuOpen && !isWorkspace && (
        <div
          ref={mobileMenuRef}
          className="public-mobile-menu absolute left-4 right-4 top-full mt-3 overflow-hidden rounded-2xl border border-sky-400/20 bg-[#050b1d] p-4 shadow-2xl shadow-black/50 lg:hidden"
        >
          <div className="max-h-[calc(100vh-7rem)] overflow-y-auto overscroll-contain">
            {mobileGroups.map((group) => (
              <div key={group.label} className="border-b border-sky-400/15 py-3 last:border-b-0">
                <button
                  type="button"
                  onClick={() => toggleMobileGroup(group.label)}
                  className="mb-3 flex w-full items-center justify-between rounded-xl px-2 py-2 text-left text-[10px] font-black uppercase tracking-[0.3em] text-sky-300 hover:bg-sky-500/10"
                >
                  {group.label}
                  <span className="flex h-8 w-8 items-center justify-center rounded-full border border-sky-400/30 bg-sky-500/15 text-sky-100">
                    <ChevronDown className={`h-5 w-5 transition-transform ${mobileOpenGroups[group.label] ? 'rotate-180' : ''}`} />
                  </span>
                </button>
                {mobileOpenGroups[group.label] && (
                  <div className="grid gap-1">
                    {group.items.map((item) => (
                      <MobileMenuRow
                        key={item.path}
                        item={item}
                        active={isActivePath(location.pathname, item)}
                        onNavigate={() => setMobileMenuOpen(false)}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link
              to="/enroll"
              onClick={() => setMobileMenuOpen(false)}
              className="mt-4 flex items-center justify-center rounded-full bg-brand-primary px-6 py-4 text-sm font-bold uppercase tracking-widest text-white"
            >
              Apply Now
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
